import JSONModel from "sap/ui/model/json/JSONModel";
import Event from "sap/ui/base/Event";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import BaseController from "de/realcore/realseats/controller/BaseController";
import FilterBar from "sap/ui/comp/filterbar/FilterBar";
import Table from "sap/m/Table";
import type FilterItem from "sap/ui/comp/filterbar/FilterItem";
import MultiComboBox from "sap/m/MultiComboBox";
import CheckBox from "sap/m/CheckBox";
import DatePicker from "sap/m/DatePicker";
import ApiService from "../src/api/ApiService";
import formatter from "../src/utils/formatter";
import SearchField from "sap/m/SearchField";
import Control from "sap/ui/core/Control";
import Button from "sap/m/Button";
import Fragment from "sap/ui/core/Fragment";
import { Route$MatchedEvent } from "sap/ui/core/routing/Route";
import MessageToast from "sap/m/MessageToast";
import { HomeZoneAuthUtil } from "../src/utils/HomeZoneAuthUtil";
import { getMaxBookingDate } from "../src/utils/TimeUtils";
import { isSeatFullyBookedForDay } from "../src/utils/BookingUtils";

/**
 * @namespace de.realcore.realseats.controller
 */
export default class FreeSpaces extends BaseController {
    private readonly formatter = formatter;
    private oldFav!: string;
    private floorplansModel!: JSONModel;
    private FreeespacesModel!: JSONModel;
    private _oFilterBar!: FilterBar;
    private oldDate!: string;
    private _oTable!: Table;
    private authToken: string;
    private allFloorplans: any[] = []; // Cache all floorplans
    private allLocations: any[] = []; // Cache all locations
    private apiService: ApiService;
    private readonly processingFavorites: Set<string> = new Set(); // Track ongoing favorite operations
    private isUpdatingFromHashChange: boolean = false; // Flag to prevent URL updates during hash change handling

    public onInit(): void {
        const oView = this.getView();

        // Initialize API service
        this.apiService = new ApiService(this.getOwnerComponent());

        // Initialize HomeZoneAuthUtil with API service
        HomeZoneAuthUtil.initialize(this.apiService);

        // Initialize models for hierarchical filtering
        this.initializeFilterModels();

        // Initialize DialogData model for date picker constraints
        const dialogData = {
            minDate: new Date(),
            maxDate: new Date(getMaxBookingDate()) // Create fresh Date object to prevent mutation
        };
        const dialogDataModel = new JSONModel(dialogData);
        oView.setModel(dialogDataModel, "DialogData");

        // Initialize date picker with current date (will be overridden by route parameters if provided)
        const datePicker = oView.byId("idFreeSpacesDatePicker") as DatePicker;
        if (datePicker) {
            const today = new Date();
            datePicker.setDateValue(today);
            console.log("Date picker initialized with today's date:", today);
        }

        this._oFilterBar = oView.byId("freeSpacesFilterBar") as FilterBar;
        this._oTable = oView.byId("roomTable") as Table;

        // Set up routing to handle date query parameters
        this.getOwnerComponent()
            .getRouter()
            .getRoute("FreeSpaces")
            .attachPatternMatched(this._onMatched.bind(this));

        // Listen for hash changes to handle browser navigation
        window.addEventListener("hashchange", this._onHashChange.bind(this));

        // Load data in sequence: first locations/floors, then free spaces
        this.loadDataSequentially().catch((error) => {
            console.error("Error loading data:", error);
        });
    }

    private async _onMatched(oEvent: Route$MatchedEvent): Promise<void> {
        // Extract date from query parameters if provided
        const routeArguments = oEvent.getParameter("arguments") as any;
        const queryParams = routeArguments["?query"] || {};
        const dateParam = queryParams.date;

        console.log("FreeSpaces _onMatched called with date parameter:", dateParam);

        // Set the date picker value based on URL parameter
        const datePicker = this.getView().byId("idFreeSpacesDatePicker") as DatePicker;
        if (dateParam && datePicker) {
            try {
                // Parse the date parameter (expected format: YYYY-MM-DD) using local timezone
                const [year, month, day] = dateParam.split("-").map(Number);
                const parsedDate = new Date(year, month - 1, day); // month is 0-indexed
                if (!isNaN(parsedDate.getTime())) {
                    // Get current date to compare
                    const currentDate = datePicker.getDateValue();
                    const currentDateString = currentDate
                        ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`
                        : null;

                    console.log(
                        "Current date in picker:",
                        currentDateString,
                        "New date:",
                        dateParam
                    );

                    // Only update if the date is actually different
                    if (currentDateString !== dateParam) {
                        datePicker.setDateValue(parsedDate);
                        console.log(
                            "Date parameter received and set in FreeSpaces:",
                            dateParam,
                            parsedDate
                        );

                        // Add a small delay to ensure the date picker value is properly set
                        await new Promise((resolve) => setTimeout(resolve, 50));

                        // Trigger data refresh with new date
                        await this.FetchFreeSpaces();
                        console.log("Data refreshed after date parameter update");
                    } else {
                        console.log("Date parameter matches current date, no update needed");
                    }
                }
            } catch (error) {
                console.error("Error parsing date parameter in FreeSpaces:", dateParam, error);
            }
        }
    }

    private _onHashChange(): void {
        const currentHash = window.location.hash;
        if (!this._shouldProcessHash(currentHash)) {
            return;
        }
        console.log("Hash change detected on FreeSpaces with date parameter");

        const dateParam = this._extractDateParamFromHash(currentHash);
        if (!dateParam) {
            return;
        }
        console.log("Found date parameter in URL:", dateParam);

        this.isUpdatingFromHashChange = true;
        this._updateDatePickerFromHash(dateParam).finally(() => {
            this.isUpdatingFromHashChange = false;
        });
    }

    private _shouldProcessHash(hash: string): boolean {
        return hash.includes("#freespaces") && !hash.includes("/seats") && hash.includes("date=");
    }

    private _extractDateParamFromHash(hash: string): string | null {
        const dateRegex = /[?&]date=([^&]+)/;
        const dateMatch = dateRegex.exec(hash);
        return dateMatch ? dateMatch[1] : null;
    }

    private async _updateDatePickerFromHash(dateParam: string): Promise<void> {
        const datePicker = this.getView()?.byId("idFreeSpacesDatePicker") as DatePicker;
        if (!datePicker || !dateParam) {
            return;
        }
        try {
            const [year, month, day] = dateParam.split("-").map(Number);
            const parsedDate = new Date(year, month - 1, day);
            if (isNaN(parsedDate.getTime())) {
                return;
            }
            const currentDate = datePicker.getDateValue();
            const currentDateString = currentDate
                ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`
                : null;
            if (currentDateString === dateParam) {
                return;
            }
            console.log("Updating date picker from hash change:", dateParam);
            datePicker.setDateValue(parsedDate);
            await this.FetchFreeSpaces().catch((error) => {
                console.error("Error refreshing data after hash change:", error);
            });
        } catch (error) {
            console.error("Error parsing date from hash:", dateParam, error);
        }
    }

    private updateUrlWithCurrentDate(): void {
        // Get the current date from the date picker
        const datePicker = this.getView()?.byId("idFreeSpacesDatePicker") as DatePicker;
        const selectedDate = datePicker?.getDateValue();

        if (selectedDate) {
            // Format date as YYYY-MM-DD using local timezone
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
            const day = String(selectedDate.getDate()).padStart(2, "0");
            const dateString = `${year}-${month}-${day}`;

            // Update the URL hash without triggering navigation
            const newHash = `#freespaces?date=${dateString}`;

            // Use replaceState to avoid adding to browser history
            window.history.replaceState(null, "", newHash);

            console.log("Updated URL with current date:", dateString);
        }
    }

    private async loadDataSequentially(): Promise<void> {
        try {
            // Initialize the free spaces model with loading state
            this.FreeespacesModel = new JSONModel({
                freeSpaces: [],
                totalRooms: 0,
                totalSeats: 0,
                isLoading: true
            });
            this.getView().setModel(this.FreeespacesModel, "FreeSpacesModel");

            // Load locations and floor plans
            await this.fetchLocations();

            // Load free spaces data (and update the filter properly)
            await this.FetchFreeSpaces();

            // Update URL with current date (will be today's date initially)
            this.updateUrlWithCurrentDate();
        } catch (error) {
            console.error("Error in loadDataSequentially:", error);
            // Ensure loading state is cleared even on error
            if (this.FreeespacesModel) {
                this.FreeespacesModel.setProperty("/isLoading", false);
            }
        }
    }

    private initializeFilterModels(): void {
        const oView = this.getView();

        // Initialize models for filter dependencies
        const locationFloorModel = new JSONModel({
            items: [],
            selectedKeys: [],
            itemCount: 0,
            itemUnit: "items"
        });

        oView.setModel(locationFloorModel, "LocationFloorModel");
    }

    private async fetchLocations(): Promise<void> {
        try {
            const oComponent = this.getOwnerComponent();
            this.authToken = await oComponent.getAccessToken();

            // Fetch all locations from the backend
            this.allLocations = await this.apiService.location.fetchAllLocations();

            // Cache all floorplans to avoid repeated API calls
            this.allFloorplans = await this.apiService.floorplan.fetchAll();
            console.log("Cached floorplans:", this.allFloorplans);

            // Create FloorPlansModel for consistency with other parts of the application
            this.floorplansModel = new JSONModel({ floorplans: this.allFloorplans });
            this.getView().setModel(this.floorplansModel, "FloorPlansModel");

            // Initialize summary
            this.updateLocationFloorSummary();
        } catch (error) {
            console.error("Error fetching locations and floorplans:", error);
        }
    }

    /**
     * Creates floor items for the unified filter (locations removed from selectable options)
     * Shows all floors initially, then filters to only floors with available seats when seat data is available
     */
    private async createLocationFloorItems(
        locations: any[],
        availableSeats: any[] = []
    ): Promise<void> {
        try {
            const combinedItems: any[] = [];

            // Create a set of floor IDs that have available seats
            const floorsWithSeats = new Set<string>();
            availableSeats.forEach((seat) => {
                if (seat.room?.floorPlan?.id) {
                    floorsWithSeats.add(seat.room.floorPlan.id);
                }
            });

            // only include floors that have available seats when seat data is available
            this.allFloorplans.forEach((floor) => {
                const hasSeatsOnFloor = floorsWithSeats.has(floor.id);

                // If we have seat data, ONLY include floors that actually have available seats
                // If no seat data yet, show all floors initially
                const shouldIncludeFloor = availableSeats.length === 0 || hasSeatsOnFloor;

                if (shouldIncludeFloor) {
                    const location = floor.location;

                    // Show just the floor name without location prefix
                    const displayText = floor.name;

                    const item = {
                        key: `floor-${floor.id}`,
                        text: displayText,
                        groupName: location.name,
                        type: "floor",
                        locationId: location.id,
                        floorId: floor.id,
                        hasAvailableSeats: hasSeatsOnFloor
                    };
                    combinedItems.push(item);
                }
            });

            // Sort by group name (location) then by floor name
            combinedItems.sort((a, b) => {
                if (a.groupName !== b.groupName) {
                    return a.groupName.localeCompare(b.groupName);
                }
                return a.text.localeCompare(b.text);
            });

            const locationFloorModel = this.getView().getModel("LocationFloorModel") as JSONModel;
            locationFloorModel.setProperty("/items", combinedItems);
            locationFloorModel.setProperty("/itemCount", combinedItems.length);
            locationFloorModel.setProperty(
                "/itemUnit",
                combinedItems.length === 1 ? "item" : "items"
            );
        } catch (error) {
            console.error("Error creating location-floor items:", error);
        }
    }

    public onDatePickerChange(): void {
        // Set loading state when date changes
        if (this.FreeespacesModel) {
            this.FreeespacesModel.setProperty("/isLoading", true);
        }

        // When date changes, refresh the free spaces and update location/floor filter
        this.FetchFreeSpaces().catch((error) => {
            console.error("Error fetching free spaces after date change:", error);
            // Ensure loading state is cleared on error
            if (this.FreeespacesModel) {
                this.FreeespacesModel.setProperty("/isLoading", false);
            }
        });

        // Update URL hash to reflect the selected date for sharing URLs and so on (only if not updating from hash change)
        if (!this.isUpdatingFromHashChange) {
            this.updateUrlWithCurrentDate();
        }
    }

    public onDeskSearchFieldSearch(oEvent: Event): void {
        // Trigger the main search when search button is pressed
        this.applyClientSideFilters();
    }

    public onDeskSearchFieldLiveChange(oEvent: Event): void {
        this.applyClientSideFilters();
    }

    public onFavoriteCheckBoxChange(oEvent: Event): void {
        // Apply client-side filtering instead of making API requests
        this.applyClientSideFilters();
    }

    /**
     * Filter rooms based on department access and bookable status
     * Uses consistent availability logic with detail view by checking business hours availability
     */
    public async FetchFreeSpaces(favoriteCase: string = "all"): Promise<void> {
        console.log("FetchFreeSpaces called with favoriteCase:", favoriteCase);

        // Set loading state at the beginning
        if (this.FreeespacesModel) {
            this.FreeespacesModel.setProperty("/isLoading", true);
        }

        try {
            const oComponent = this.getOwnerComponent();
            this.authToken = await oComponent.getAccessToken();

            const oDatePicker = this.getView().byId("idFreeSpacesDatePicker") as DatePicker;
            const oDate = oDatePicker.getDateValue() as Date;

            console.log("FetchFreeSpaces: Date picker value:", oDate);

            if (!oDate) {
                console.warn("No date selected for fetching free spaces.");
                return;
            }

            const selectedDate = formatter.formatLocalDateTime(oDate);

            if (selectedDate === this.oldDate && favoriteCase === this.oldFav) {
                console.log("same date", "same fav");
            } else {
                // Get ALL seats instead of just available ones to apply consistent logic
                const allSeats = await this.apiService.seat.fetchAll();

                // Get ALL bookings for the selected date
                const allBookings = await this.apiService.booking.fetchAllBookings();

                // Filter bookings for the selected date
                const selectedDateString = formatter.formatDate(selectedDate);
                const todaysBookings = allBookings.filter((booking: any) => {
                    const bookingDate = new Date(booking.startTime);
                    const bookingDateString = formatter.formatDate(
                        formatter.formatLocalDateTime(bookingDate)
                    );
                    return bookingDateString === selectedDateString && !booking.cancelled;
                });

                // Apply department-based and home zone filtering first
                const currentUser = await oComponent.getCurrentUser();
                const authorizedSeats = await this.filterSpacesByDepartment(allSeats, currentUser);

                // Apply favorite filtering based on favoriteCase
                let filteredSeats = authorizedSeats;
                if (favoriteCase === "yes") {
                    // Only show favorite seats
                    await this.markFavoriteSeats();
                    filteredSeats = authorizedSeats.filter((seat: any) => seat.isFavorite === true);
                } else if (favoriteCase === "no") {
                    // Only show non-favorite seats
                    await this.markFavoriteSeats();
                    filteredSeats = authorizedSeats.filter((seat: any) => seat.isFavorite !== true);
                }

                // Filter seats using the same availability logic as detail view
                const availableSeats = filteredSeats.filter((seat: any) => {
                    // Get all bookings for this specific seat
                    const seatBookings = todaysBookings.filter(
                        (booking: any) => booking.seat?.id === seat.id
                    );

                    // Use the same business hours availability check as detail view
                    return !isSeatFullyBookedForDay(seatBookings, oDate);
                });

                // Group seats by room to show unique rooms
                const uniqueRooms = this.groupSeatsByRoom(availableSeats);

                // Calculate summary statistics
                const totalRooms = uniqueRooms.length;
                const totalSeats = uniqueRooms.reduce(
                    (sum: number, room: any) => sum + (room.seatCount || 0),
                    0
                );

                this.FreeespacesModel = new JSONModel({
                    freeSpaces: uniqueRooms,
                    totalRooms: totalRooms,
                    totalSeats: totalSeats,
                    isLoading: false // Set loading to false when data is ready
                });
                this.getView().setModel(this.FreeespacesModel, "FreeSpacesModel");

                // Update location and floor filter to only show floors with available seats
                await this.updateLocationFloorFilterWithAvailableSeats(availableSeats);

                console.log("Free spaces fetched successfully:", uniqueRooms);

                this.oldDate = selectedDate;
                console.log("Selected date:", this.oldDate);
                this.oldFav = favoriteCase;

                // Mark favorite seats if not already done
                if (favoriteCase === "all") {
                    await this.markFavoriteSeats();
                }
            }
        } catch (error) {
            console.error(`Error fetching ${favoriteCase} spaces:`, error);
            const errorMessage =
                (error as Error)?.message || `Unknown error fetching ${favoriteCase} spaces`;
            MessageToast.show(`Failed to load spaces: ${errorMessage}`);
        } finally {
            // Ensure loading state is cleared even if there's an error
            if (this.FreeespacesModel) {
                this.FreeespacesModel.setProperty("/isLoading", false);
            }
        }
    }

    /**
     * Groups seats by room to show unique rooms with seat count
     */
    private groupSeatsByRoom(seats: any[]): any[] {
        const roomMap = new Map<string, any>();

        seats.forEach((seat: any) => {
            const roomId = seat.room?.id;
            const roomName = seat.room?.name;

            if (!roomId || !roomName) {
                return; // Skip seats without room information
            }

            if (!roomMap.has(roomId)) {
                // Create a new room entry
                roomMap.set(roomId, {
                    id: seat.id, // Use first seat's ID for navigation
                    room: seat.room,
                    name: `${seat.room.name} (1 seat)`, // Initial count
                    position: "", // Not applicable for room view
                    isFavorite: seat.isFavorite || false,
                    seatCount: 1,
                    seats: [seat] // Keep track of individual seats
                });
            } else {
                // Update existing room entry
                const existingRoom = roomMap.get(roomId);
                existingRoom.seatCount += 1;
                existingRoom.name = `${seat.room.name} (${existingRoom.seatCount} seat${existingRoom.seatCount === 1 ? "" : "s"})`;
                existingRoom.seats.push(seat);
                // Room is favorite if ANY seat in the room is favorite
                existingRoom.isFavorite = existingRoom.isFavorite || seat.isFavorite || false;
            }
        });

        return Array.from(roomMap.values());
    }

    /**
     * Filters free spaces based on department rules and home zone authorization:
     * 1. Only show bookable rooms
     * 2. If room belongs to department, user must belong to same department
     * 3. If room has no department restriction, show to all users
     * 4. Exclude seats with name "ALL" as they are not bookable
     * 5. Only show seats that the user is authorized to access based on home zones
     */
    private async filterSpacesByDepartment(freeSpaces: any[], currentUser: any): Promise<any[]> {
        if (!freeSpaces || !Array.isArray(freeSpaces)) {
            return [];
        }

        const userDepartmentId = currentUser?.department?.id;

        // Get user's home zones for authorization check
        const authorizedSpaces = await HomeZoneAuthUtil.filterSpacesByHomeZone(
            freeSpaces,
            (space: any) => space?.room?.homeZones || []
        );

        return authorizedSpaces.filter((space: any) => {
            // Exclude seats with name "ALL" as they represent entire rooms and are not bookable
            if (space.name === "ALL") {
                return false;
            }

            // Check if room is bookable
            const isBookable = space.room?.bookable !== false;

            if (!isBookable) {
                return false;
            }

            // Check department restrictions
            const roomDepartmentId = space.room?.department?.id;

            // If room has no department restriction, show to all users
            if (!roomDepartmentId) {
                return true;
            }

            // If room has department restriction, user must belong to same department
            return userDepartmentId === roomDepartmentId;
        });
    }

    public async fetchAllFloorPlans(): Promise<void> {
        if (this.allFloorplans && this.allFloorplans.length > 0) {
            this.floorplansModel = new JSONModel({ floorplans: this.allFloorplans });
            this.getView().setModel(this.floorplansModel, "FloorPlansModel");
            return;
        }
        try {
            const apiService = this.getApiService();
            this.allFloorplans = await apiService.floorplan.fetchAll();
            this.floorplansModel = new JSONModel({ floorplans: this.allFloorplans });
            this.getView().setModel(this.floorplansModel, "FloorPlansModel");
        } catch (error) {
            console.error("Error fetching floor plans:", error);
        }
    }

    public async addFavoriteSeat(seatId: string): Promise<void> {
        await this.apiService.employee.addFavoriteSeat(seatId);
    }

    public async removeFavoriteSeat(seatId: string): Promise<void> {
        await this.apiService.employee.removeFavoriteSeat(seatId);
    }

    public onSearch(): void {
        this.applyClientSideFilters();
    }

    /**
     * Apply client-side filters to the table without making API requests
     */
    private applyClientSideFilters(): void {
        const aFilterItems = this._oFilterBar.getAllFilterItems(true) as FilterItem[];
        const aTableFilters = aFilterItems.reduce((aResult: Filter[], oFilterItem: FilterItem) => {
            const sFilterName = oFilterItem.getName();
            const oFilterControl = oFilterItem.getControl();

            switch (sFilterName) {
                case "LocationFloor": {
                    const control = oFilterControl as MultiComboBox;
                    const aSelectedKeys = control.getSelectedKeys?.();
                    if (aSelectedKeys && aSelectedKeys.length > 0) {
                        // Only handle floor selections since locations are no longer selectable
                        const selectedFloorIds: string[] = [];

                        aSelectedKeys.forEach((key) => {
                            if (key.startsWith("floor-")) {
                                selectedFloorIds.push(key.replace("floor-", ""));
                            }
                        });

                        // Add specific floor filters
                        if (selectedFloorIds.length > 0) {
                            const floorFilters: Filter[] = selectedFloorIds.map(
                                (floorId) =>
                                    new Filter("room/floorPlan/id", FilterOperator.EQ, floorId)
                            );

                            // Combine all floor filters with OR logic
                            if (floorFilters.length === 1) {
                                aResult.push(floorFilters[0]);
                            } else {
                                aResult.push(
                                    new Filter({
                                        filters: floorFilters,
                                        and: false // OR logic
                                    })
                                );
                            }
                        }

                        console.log("Floor filter applied:", { selectedFloorIds });
                    }
                    break;
                }

                case "Desk": {
                    const control = oFilterControl as SearchField;
                    const sValue = control.getValue?.();
                    if (sValue) {
                        aResult.push(new Filter("name", FilterOperator.Contains, sValue));
                        console.log("Desk filter applied:", sValue);
                    }
                    break;
                }

                case "Favorite": {
                    const control = oFilterControl as CheckBox;
                    const isSelected = control.getSelected?.();

                    if (isSelected) {
                        // Show only favorite seats using client-side filter
                        aResult.push(new Filter("isFavorite", FilterOperator.EQ, true));
                        console.log("Favorite filter applied: showing only favorites");
                    }
                    // If not selected, show all (no filter needed)
                    break;
                }
            }
            return aResult;
        }, []);

        const oTableBinding = this._oTable.getBinding("items") as ListBinding;

        // Debug: Log a sample of data structure before filtering
        const oModel = this.getView().getModel("FreeSpacesModel") as JSONModel;
        const freeSpacesData = oModel.getProperty("/freeSpaces");
        if (freeSpacesData && freeSpacesData.length > 0) {
            console.log("Filter Debug - Sample data structure:", {
                firstItem: freeSpacesData[0],
                itemCount: freeSpacesData.length
            });
        }

        console.log("Filter Debug - Applying filters:", aTableFilters);
        oTableBinding.filter(aTableFilters);
    }

    public onRoomSelectionForSeats(oEvent: Event) {
        const oSource = oEvent.getSource<Control>();
        const oContext = oSource.getBindingContext("FreeSpacesModel");

        if (oContext) {
            const roomData = oContext.getObject() as { room?: { id: string; name: string } };
            if (roomData?.room?.id) {
                // Get the currently selected date from the date picker
                const oDatePicker = this.getView().byId("idFreeSpacesDatePicker") as DatePicker;
                const selectedDate = oDatePicker?.getDateValue();

                // Add date as query parameter if available
                if (selectedDate) {
                    // Format date as YYYY-MM-DD for URL using local timezone
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
                    const day = String(selectedDate.getDate()).padStart(2, "0");
                    const dateString = `${year}-${month}-${day}`;
                    // Manually construct the hash with query parameters
                    const hash = `#freespaces/${roomData.room.id}/seats?date=${dateString}`;
                    window.location.hash = hash;
                } else {
                    // Navigate without date parameter
                    const routeParams = {
                        room: roomData.room.id
                    };
                    this.navTo("FreeSpacesSeats", routeParams);
                }
            }
        }
    }

    private async markFavoriteSeats(): Promise<void> {
        if (!this.FreeespacesModel) {
            return;
        }

        try {
            // Call the API and get the raw favorite seats
            const favoriteSeatsResult = await this.apiService.employee.fetchFavoriteSeats();

            const favoriteSeats: Array<{ id: string }> = Array.isArray(favoriteSeatsResult)
                ? favoriteSeatsResult
                : [];
            // Extract IDs into a Set for quick lookup
            const favoriteSeatIds = new Set(favoriteSeats.map((seat: { id: string }) => seat.id));

            const freeSpaces = this.FreeespacesModel.getProperty("/freeSpaces");
            if (!freeSpaces || !Array.isArray(freeSpaces)) {
                return;
            }

            // Mark seats as favorite and update room-level favorite status
            freeSpaces.forEach(
                (roomData: {
                    id: string;
                    isFavorite?: boolean;
                    seats: Array<{ id: string; isFavorite?: boolean }>;
                }) => {
                    let hasAnyFavoriteSeat = false;

                    // Check if this is a grouped room data with seats array
                    if (roomData.seats && Array.isArray(roomData.seats)) {
                        // Mark individual seats and check if any are favorites
                        roomData.seats.forEach((seat: { id: string; isFavorite?: boolean }) => {
                            seat.isFavorite = favoriteSeatIds.has(seat.id);
                            if (seat.isFavorite) {
                                hasAnyFavoriteSeat = true;
                            }
                        });

                        // Room is favorite if ANY seat in the room is favorite
                        roomData.isFavorite = hasAnyFavoriteSeat;
                    } else {
                        // Fallback for single seat data
                        roomData.isFavorite = favoriteSeatIds.has(roomData.id);
                    }
                }
            );

            // Update the model so UI bindings refresh
            this.FreeespacesModel.setProperty("/freeSpaces", freeSpaces);

            console.log("Favorite seats marked for rooms:", freeSpaces);
        } catch (error) {
            console.error("Error marking favorite seats:", error);
            const errorMessage =
                (error as Error)?.message || "Unknown error occurred while loading favorite seats";
            MessageToast.show(`Failed to load favorite seats: ${errorMessage}`);
        }
    }

    public formatFavoriteIcon(isFavorite: boolean): string {
        const icons = this.getFavoriteIconMap();
        return icons[String(isFavorite)];
    }

    private getFavoriteIconMap(): Record<string, string> {
        return {
            true: "sap-icon://heart",
            false: "sap-icon://heart-2"
        };
    }

    public async onFavoriteIconPress(oEvent: Event): Promise<void> {
        const oButton = oEvent.getSource<Button>();
        const oContext = oButton.getBindingContext("FreeSpacesModel");

        if (!oContext) {
            return;
        }

        const roomData = oContext.getObject() as {
            id: string;
            isFavorite?: boolean;
            seats: Array<{ id: string; isFavorite?: boolean }>;
            room?: { id: string; name: string };
        };

        // Create a unique key for this room's favorite operation
        const operationKey = roomData.room?.id || roomData.id;

        // Prevent concurrent operations on the same room
        if (this.processingFavorites.has(operationKey)) {
            MessageToast.show(this.getText("processingFavoriteOperation"));
            return;
        }

        try {
            // Mark operation as in progress
            this.processingFavorites.add(operationKey);

            // Since we're showing rooms with multiple seats, we need to handle all seats in the room
            if (roomData.seats && roomData.seats.length > 0) {
                const isCurrentlyFavorite = roomData.isFavorite;

                // Toggle favorite status for all seats in this room
                // Process sequentially to avoid race conditions (TODO: check later on in backend why this happens)
                for (const seat of roomData.seats) {
                    try {
                        if (isCurrentlyFavorite) {
                            console.log(
                                "FreeSpaces.controller: Removing favorite for seat:",
                                seat.id
                            );
                            await this.apiService.employee.removeFavoriteSeat(seat.id);
                        } else {
                            console.log(
                                "FreeSpaces.controller: Adding favorite for seat:",
                                seat.id
                            );
                            await this.apiService.employee.addFavoriteSeat(seat.id);
                        }
                    } catch (seatError) {
                        console.warn(
                            "FreeSpaces.controller: Error with seat",
                            seat.id,
                            ":",
                            seatError
                        );
                        // Continue with other seats even if one fails
                    }
                }

                console.log("FreeSpaces.controller: All seat operations completed");

                // Update the room's favorite status
                roomData.isFavorite = !isCurrentlyFavorite;

                // Update individual seat favorite status
                roomData.seats.forEach((seat) => {
                    seat.isFavorite = !isCurrentlyFavorite;
                });

                // Show success message
                const action = isCurrentlyFavorite ? "removed from" : "added to";
                MessageToast.show(`Room ${roomData.room?.name || "seats"} ${action} favorites`);

                // Refresh the model to update UI
                oContext.getModel()?.refresh(true);
                return;
            }

            // Fallback for single seat (if any)
            if (roomData.isFavorite) {
                await this.apiService.employee.removeFavoriteSeat(roomData.id);
                roomData.isFavorite = false;
                MessageToast.show(this.getText("seatRemovedFromFavorites"));
            } else {
                await this.apiService.employee.addFavoriteSeat(roomData.id);
                roomData.isFavorite = true;
                MessageToast.show(this.getText("seatAddedToFavorites"));
            }

            // Refresh the model to update UI
            oContext.getModel()?.refresh(true);
        } catch (error) {
            console.error("FreeSpaces.controller: Error updating favorite seat:", error);

            const errorMessage = (error as Error)?.message;
            if (errorMessage?.includes("duplicate key")) {
                MessageToast.show(this.getText("seatAlreadyInFavorites"));
            } else if (errorMessage?.includes("constraint")) {
                MessageToast.show(this.getText("unableToUpdateFavorites"));
            } else {
                // Show the actual server response if available, or fallback to generic message
                const displayMessage = errorMessage
                    ? `Failed to update favorites: ${errorMessage}`
                    : this.getText("errorUpdatingFavorites");
                MessageToast.show(displayMessage);
            }
        } finally {
            this.processingFavorites.delete(operationKey);
        }
    }

    /**
     * Updates floor summary counts for the overview panel
     */
    private updateLocationFloorSummary(): void {
        const locationFloorModel = this.getView().getModel("LocationFloorModel") as JSONModel;

        const selectedKeys = locationFloorModel.getProperty("/selectedKeys") || [];
        const allItems = locationFloorModel.getProperty("/items") || [];

        // Get unique floors and their locations from all items (only floors are selectable now)
        const allFloors = allItems; // All items are floors now
        const selectedFloors = selectedKeys.filter((key: string) => key.startsWith("floor-"));

        // Calculate floor count
        const floorCount = selectedFloors.length > 0 ? selectedFloors.length : allFloors.length;
        const floorUnit = floorCount === 1 ? "floor" : "floors";

        // Calculate unique location count from the floors
        const uniqueLocationIds = new Set();
        const floorsToConsider =
            selectedFloors.length > 0
                ? allFloors.filter((item: any) => selectedKeys.includes(item.key))
                : allFloors;

        floorsToConsider.forEach((item: any) => {
            if (item.locationId) {
                uniqueLocationIds.add(item.locationId);
            }
        });

        const locationCount = uniqueLocationIds.size;
        const locationUnit = locationCount === 1 ? "location" : "locations";

        // Update the model
        locationFloorModel.setProperty("/locationCount", locationCount);
        locationFloorModel.setProperty("/locationUnit", locationUnit);
        locationFloorModel.setProperty("/floorCount", floorCount);
        locationFloorModel.setProperty("/floorUnit", floorUnit);
    }

    /**
     * Updates the location-floor filter to only show floors that have available seats
     */
    private async updateLocationFloorFilterWithAvailableSeats(
        availableSeats: any[]
    ): Promise<void> {
        try {
            // Use cached locations data to avoid duplicate API calls
            // Update the location-floor items with current available seats data
            await this.createLocationFloorItems(this.allLocations, availableSeats);

            // Update the summary after filter items are updated
            this.updateLocationFloorSummary();

            console.log(
                "Updated location-floor filter with available seats:",
                availableSeats.length
            );
        } catch (error) {
            console.error("Error updating location-floor filter with available seats:", error);
        }
    }

    // ==============================
    // Location and Floor Filter Methods
    // ==============================

    /**
     * Event handler for combined location-floor MultiComboBox selection change
     * @param oEvent - The selection change event
     */
    public onLocationFloorMultiComboBoxSelectionChange(oEvent: Event): void {
        const oMultiComboBox = oEvent.getSource() as MultiComboBox;
        const aSelectedKeys = oMultiComboBox.getSelectedKeys();

        // Update the combined model
        const locationFloorModel = this.getView().getModel("LocationFloorModel") as JSONModel;
        locationFloorModel.setProperty("/selectedKeys", aSelectedKeys);

        // Apply filters
        this.applyClientSideFilters();
    }

    /**
     * Event handler for clear filters button press
     * Resets all filter selections and reloads data
     */
    public onClearFilters(): void {
        const oView = this.getView();

        // Clear date picker
        const oDatePicker = oView.byId("idFreeSpacesDatePicker") as DatePicker;
        if (oDatePicker) {
            oDatePicker.setValue(new Date().toISOString().split("T")[0]);
        }

        // Clear combined location-floor selection
        const oLocationFloorMultiComboBox = oView.byId(
            "idLocationFloorMultiComboBox"
        ) as MultiComboBox;
        if (oLocationFloorMultiComboBox) {
            oLocationFloorMultiComboBox.setSelectedKeys([]);
        }
        const locationFloorModel = oView.getModel("LocationFloorModel") as JSONModel;
        locationFloorModel.setProperty("/selectedKeys", []);

        // Clear desk search
        const oDeskSearchField = oView.byId("idDeskSearchField") as SearchField;
        if (oDeskSearchField) {
            oDeskSearchField.setValue("");
        }

        // Clear favorite checkbox
        const oFavoriteCheckBox = oView.byId("idFavoriteFreeSpacesCheckBox") as CheckBox;
        if (oFavoriteCheckBox) {
            oFavoriteCheckBox.setSelected(false);
        }

        // Reload data with cleared filters
        this.applyClientSideFilters();

        MessageToast.show(this.getText("allFiltersCleared"));
    }

    /**
     * Shows a popover with all room types for a room when the link is clicked
     * @param oEvent The press event
     */
    public async onLinkRoomTypesPress(oEvent: Event): Promise<void> {
        const oLink = oEvent.getSource() as Control;
        const oContext = oLink.getBindingContext("FreeSpacesModel");

        if (!oContext) {
            return;
        }

        const oRoom = oContext.getObject() as any;
        const aRoomTypes = oRoom.room?.roomTypes || [];

        // Create popover if it doesn't exist
        if (!this._roomTypesPopover) {
            this._roomTypesPopover = (await Fragment.load({
                id: this.getView()?.getId(),
                name: "de.realcore.realseats.view.fragment.RoomTypesPopover",
                controller: this
            })) as any;
            this.getView()?.addDependent(this._roomTypesPopover);
        }

        // Set the room types data to the popover
        const oPopoverModel = new JSONModel({ roomTypes: aRoomTypes });
        this._roomTypesPopover.setModel(oPopoverModel, "PopoverRoomTypesModel");

        // Open popover
        this._roomTypesPopover.openBy(oLink);
    }

    public onExit(): void {
        window.removeEventListener("hashchange", this._onHashChange.bind(this));
    }

    private _roomTypesPopover: any;
}
